import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../../components/Sidebar";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomButton from "../../../components/CustomButton";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import { clearHomePageInfosClientCache, getHomePageInfos, getTradingAccountsTrx, createInternalTransferMoneyOperation } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import WalletController from "../../../controller/walletController";
import { getPermissionControllers, hasCustomerPermissionEither, isAccountApprovalLocked, isAgentWithdrawalDisabled } from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import { AccountType } from "../../../utils/enums";
import { useToast } from "../../../contexts/ToastContext";
import { openSupportChatOnCurrentPage } from "../../../utils/supportChatNavigation";
import { useTranslation } from "react-i18next";
import ReactCountryFlag from "react-country-flag";
import { getFlagCountryCodeForWallet } from "../../../utils/currencyFlagHelper";
import "../../css/cachIn.css";
import mt5Logo from "../../../assets/MT5.png";

/** Extract a trading account nickname from a TRX wallet item (empty when none). */
function getAccountNickname(item) {
  const raw =
    item?.account?.account_information?.NAME ||
    item?.account?.accountInformation?.name ||
    item?.NICK ||
    item?.NICK_NAME ||
    item?.nick ||
    item?.nick_name ||
    item?.NICKNAME ||
    "";
  const trimmed = String(raw).trim();
  return trimmed === "-" ? "" : trimmed;
}

/** Get wallet balance from wallet / TRX item data */
function getWalletBalance(walletData) {
  const b =
    walletData?.CURRENT_BALENCE ??
    walletData?.CURRENT_BALANCE ??
    walletData?.BALANCE ??
    walletData?.AMOUNT ??
    walletData?.amount;
  if (b == null) return 0;
  return typeof b === "string" ? parseFloat(b.replace(/,/g, "")) : parseFloat(b);
}

/** Minimum transfer amount from destination wallet currency RANGE.MIN (inclusive). */
function getWalletMinAmount(walletData) {
  const rangeRaw =
    walletData?.currencies?.RANGE ??
    walletData?.currency?.RANGE ??
    walletData?.RANGE ??
    null;
  if (rangeRaw == null) return null;
  try {
    const range =
      typeof rangeRaw === "string" ? JSON.parse(rangeRaw) : rangeRaw;
    if (range && range.MIN !== undefined && range.MIN !== null) {
      const min = parseFloat(range.MIN);
      return Number.isNaN(min) ? null : min;
    }
  } catch {
    // ignore invalid RANGE
  }
  return null;
}

/** Rebuild dropdown label after a balance change (mirrors initial load formatting). */
function buildWalletOptionLabel(item, balanceOverride) {
  const currencyType =
    item?.currencies?.TYPE ||
    item?.currency?.type ||
    item?.CURRENCY_TYPE ||
    item?.currencyType ||
    null;
  const tradingId =
    item?.account?.account_information?.TRADING_ID ||
    item?.account?.accountInformation?.tradingId ||
    item?.TRADING_ID ||
    item?.trading_id ||
    null;
  const isTradingAccount = currencyType === 3 || (currencyType === null && tradingId);
  const currencyCode =
    item?.currencies?.CURRENCY_CODE ||
    item?.currency?.currencyCode ||
    item?.CURRENCY_CODE ||
    "";
  const currencySymbol =
    item?.currencies?.CURRENCY_SYMBOL ||
    item?.currency?.currencySymbol ||
    item?.CURRENCY_SYMBOL ||
    "";
  const numericBalance = Number.isFinite(balanceOverride)
    ? Number(balanceOverride)
    : getWalletBalance(item);
  const formattedBalance = Number.isNaN(numericBalance) ? 0 : numericBalance;

  if (isTradingAccount) {
    const nickname = getAccountNickname(item);
    const accountLabel = nickname ? `${tradingId || "-"}-${nickname}` : `${tradingId || "-"}`;
    return `${accountLabel} (${formattedBalance.toFixed(2)} ${currencySymbol || currencyCode})`;
  }

  const code = currencyCode || currencySymbol || "USD";
  return `${code} (${formattedBalance.toFixed(2)} ${code})`;
}

function getCurrencyTypeValue(item) {
  const raw =
    item?.currencies?.TYPE ??
    item?.currency?.type ??
    item?.CURRENCY_TYPE ??
    item?.currencyType ??
    null;
  if (raw == null || raw === "") return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

function isFiatWalletItem(item) {
  const currencyType = getCurrencyTypeValue(item);
  const tradingId =
    item?.account?.account_information?.TRADING_ID ||
    item?.account?.accountInformation?.tradingId ||
    item?.TRADING_ID ||
    item?.trading_id ||
    null;
  return currencyType === 0 || (currencyType === null && !tradingId);
}

function isTradingAccountItem(item) {
  if (!item) return false;
  const currencyType =
    item?.currencies?.TYPE ||
    item?.currency?.type ||
    item?.CURRENCY_TYPE ||
    item?.currencyType ||
    null;
  const tradingId =
    item?.account?.account_information?.TRADING_ID ||
    item?.account?.accountInformation?.tradingId ||
    item?.TRADING_ID ||
    item?.trading_id ||
    null;
  return currencyType === 3 || (currencyType === null && !!tradingId);
}

function isUsdWalletItem(item) {
  if (!item) return false;
  const country = String(
    item?.currencies?.COUNTRY || item?.currency?.country || item?.COUNTRY || ""
  ).toUpperCase();
  const currencyCode = String(
    item?.currencies?.CURRENCY_CODE ||
      item?.currency?.currencyCode ||
      item?.CURRENCY_CODE ||
      ""
  ).toUpperCase();
  const currencySymbol =
    item?.currencies?.CURRENCY_SYMBOL ||
    item?.currency?.currencySymbol ||
    item?.CURRENCY_SYMBOL ||
    "";
  return country === "US" || currencyCode === "USD" || currencySymbol === "$";
}

function findUsdWalletOption(options) {
  if (!Array.isArray(options)) return null;
  return (
    options.find((option) => isUsdWalletItem(option?.data)) ||
    options.find((option) => /\bUSD\b/i.test(String(option?.label || ""))) ||
    options.find((option) => isFiatWalletItem(option?.data)) ||
    null
  );
}

function findWalletOptionById(options, walletId) {
  if (walletId == null || !Array.isArray(options)) return null;
  return options.find((option) => String(option.value) === String(walletId)) || null;
}

/** MT5 logo for trading accounts; country flag for fiat wallet. */
function renderAccountOptionLeading(item) {
  if (isTradingAccountItem(item)) {
    return <img src={mt5Logo} alt="MT5" className="cash-in-account-logo" />;
  }
  const flag = item ? getFlagCountryCodeForWallet(item) : null;
  if (!flag) return null;
  return (
    <ReactCountryFlag
      countryCode={flag}
      svg
      style={{ width: "20px", height: "15px", objectFit: "cover" }}
      title={flag}
    />
  );
}

export default function InternalTransfer() {
  const { t } = useTranslation();
  const { success: showSuccess, error: showError, warning: showWarning } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  const [amount, setAmount] = useState("");
  const [fromWallet, setFromWallet] = useState(null);
  const [toWallet, setToWallet] = useState(null);
  const [fromWalletOptions, setFromWalletOptions] = useState([]);
  const [toWalletOptions, setToWalletOptions] = useState([]);

  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [passwordDialogData, setPasswordDialogData] = useState(null);

  // Prefill from Accounts List deposit/withdraw actions: deposit → USD From, withdraw → USD To.
  const transferMode =
    location.state?.transferMode ||
    new URLSearchParams(location.search).get("mode") ||
    null;
  const preselectWalletId = location.state?.walletId ?? null;
  const prefillAppliedRef = useRef(false);

  const fromWalletOptionsRef = useRef([]);
  const toWalletOptionsRef = useRef([]);
  const pendingTransferRef = useRef(null);
  const balanceRefreshTimersRef = useRef([]);

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const {
    customerPerm,
    customerDevicePerm,
  } = permissions;

  // Global approval/profile lock:
  // - Profile not completed OR KYC in "Account Approval Required" → fully locked
  const approvalLocked = useMemo(() => isAccountApprovalLocked(user), [user]);

  // Direction flags match the app screen: account or device.
  // MT5 -> MT5 is checked on the account only, same as the app submit check.
  const canTransferUsdMt5 = hasCustomerPermissionEither(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5
  );
  const canTransferMt5Usd = hasCustomerPermissionEither(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_USD
  );
  const canTransferMt5Mt5 = Boolean(
    customerPerm?.hasPermission?.(
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_MT5
    )
  );

  // Any internal transfer permission. Update-leverage is unrelated and must not lock this page.
  const canInternalTransfer = !approvalLocked && (canTransferUsdMt5 || canTransferMt5Usd || canTransferMt5Mt5);

  useEffect(() => {
    fromWalletOptionsRef.current = fromWalletOptions;
  }, [fromWalletOptions]);

  useEffect(() => {
    toWalletOptionsRef.current = toWalletOptions;
  }, [toWalletOptions]);

  useEffect(() => {
    return () => {
      balanceRefreshTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      balanceRefreshTimersRef.current = [];
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setErrors({});

        // Try cached user data
        let userDataFromStorage = null;
        try {
          const storedUserData = sessionStorage.getItem("userData");
          const storedTimestamp = sessionStorage.getItem("userDataTimestamp");
          if (storedUserData && storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp, 10);
            if (age < 5 * 60 * 1000) {
              userDataFromStorage = JSON.parse(storedUserData);
              setUser(userDataFromStorage);
            }
          }
        } catch (_) {}

        let currentUser = userDataFromStorage;
        try {
          const userResponse = await getHomePageInfos();
          const userData = userResponse?.decryptedData || userResponse?.data;
          if (userData && userData.CODE === AppConstants.responseOk && userData.DATA) {
            currentUser = userData.DATA;
            setUser(currentUser);
            try {
              sessionStorage.setItem("userData", JSON.stringify(currentUser));
              sessionStorage.setItem("userDataTimestamp", Date.now().toString());
            } catch (_) {}
          }
        } catch (_) {
          // Keep cached user when request fails.
        }

        if (!currentUser) {
          setFromWalletOptions([]);
          setToWalletOptions([]);
          setLoading(false);
          return;
        }

        // Recalculate permissions with current user (mirror Flutter logic)
        const currentPermissions = getPermissionControllers(currentUser);
        const currentCanTransferUsdMt5 = hasCustomerPermissionEither(
          currentPermissions.customerPerm,
          currentPermissions.customerDevicePerm,
          PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5
        );
        const currentCanTransferMt5Usd = hasCustomerPermissionEither(
          currentPermissions.customerPerm,
          currentPermissions.customerDevicePerm,
          PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_USD
        );

        // Load wallets (trading + USD) similar to Flutter GET_TRADING_ACCOUNT_FOR_TRX
        const typeParam = [AccountType.REAL, AccountType.BROKER];
        const withUsdParam = 1;
        const walletsResponse = await getTradingAccountsTrx(typeParam, withUsdParam);
        const walletsData = walletsResponse?.decryptedData || walletsResponse?.data;

        if (walletsData && walletsData.CODE === AppConstants.responseOk && walletsData.DATA) {
          const list = Array.isArray(walletsData.DATA) ? walletsData.DATA : [];

          const fromOptions = [];
          const toOptions = [];
          let usdAddedToFromAccounts = false;
          // Checked AGENT_WITHDRAWAL_DISABLED → hide broker only as withdraw source (From).
          // Unchecked → broker From/To show normally.
          const hideBrokerWithdraw = isAgentWithdrawalDisabled(currentUser);

          list.forEach((item) => {
            // Get currency type: 0=FIAT, 3=TRADING (matches Flutter CurrencyType enum)
            const currencyType = 
              item.currencies?.TYPE ||
              item.currency?.type ||
              item.CURRENCY_TYPE ||
              item.currencyType ||
              null;

            // Get account type: 0=REAL, 1=BROKER (matches Flutter AccountType enum)
            const accountType = 
              item.account?.TYPE ||
              item.account?.type ||
              item.ACCOUNT_TYPE ||
              item.accountType ||
              null;

            // Get trading ID
            const tradingId =
              item.account?.account_information?.TRADING_ID ||
              item.account?.accountInformation?.tradingId ||
              item.TRADING_ID ||
              item.trading_id ||
              null;

            const balance =
              item.AMOUNT ||
              item.CURRENT_BALENCE ||
              item.CURRENT_BALANCE ||
              item.BALANCE ||
              0;
            const currencyCode =
              item.currencies?.CURRENCY_CODE ||
              item.currency?.currencyCode ||
              item.CURRENCY_CODE ||
              "";
            const currencySymbol =
              item.currencies?.CURRENCY_SYMBOL ||
              item.currency?.currencySymbol ||
              item.CURRENCY_SYMBOL ||
              "";

            const numericBalance =
              typeof balance === "string"
                ? parseFloat(balance.replace(/,/g, ""))
                : parseFloat(balance);
            const formattedBalance = Number.isNaN(numericBalance) ? 0 : numericBalance;

            const value = item.ID ?? item.id ?? item.WALLET_ID;

            // Match Flutter logic: Determine if this is a TRADING account or FIAT wallet
            // Use currencyType if available (3=TRADING, 0=FIAT), otherwise use tradingId as fallback
            const isTradingAccount = currencyType === 3 || (currencyType === null && tradingId);
            const isFiatWallet = currencyType === 0 || (currencyType === null && !tradingId);
            
            // For TRADING accounts: check account type (REAL or BROKER) if available
            // If accountType is null, still allow it (fallback for compatibility)
            if (isTradingAccount && (accountType === null || accountType === AccountType.REAL || accountType === AccountType.BROKER)) {
              // Show "accountNumber-Nickname (balance currency)" when a nickname exists.
              const nickname = getAccountNickname(item);
              const accountLabel = nickname ? `${tradingId || '-'}-${nickname}` : `${tradingId || '-'}`;
              const tradingLabel = `${accountLabel} (${formattedBalance.toFixed(2)} ${currencySymbol || currencyCode})`;
              const tradingOption = {
                value,
                label: tradingLabel,
                data: item,
              };

              // From = withdraw source: omit broker when AGENT_WITHDRAWAL_DISABLED is checked.
              if (!(accountType === AccountType.BROKER && hideBrokerWithdraw)) {
                fromOptions.push(tradingOption);
              }

              // To = deposit destination: broker stays available either way.
              toOptions.push({ ...tradingOption });
            } 
            // Match Flutter logic: Check if FIAT wallet (type 0)
            else if (isFiatWallet) {
              // FIAT (USD) wallet: mirror Flutter behavior with permissions
              const code = currencyCode || currencySymbol || "USD";
              const usdLabel = `${code} (${formattedBalance.toFixed(2)} ${code})`;
              const usdOption = {
                value,
                label: usdLabel,
                data: item,
              };

              // Add USD to "From" only once and only if user can transfer USD -> MT5
              if (!usdAddedToFromAccounts && currentCanTransferUsdMt5) {
                fromOptions.push(usdOption);
                usdAddedToFromAccounts = true;
              }

              // Add USD to "To" only if user can transfer MT5 -> USD (i.e. withdraw).
              // Flutter checks: wallet.currency?.type == CurrencyType.TRADING || canTransferMt5Usd
              // Since we're in FIAT block, first part is false, so check canTransferMt5Usd.
              if (currentCanTransferMt5Usd) {
                toOptions.push(usdOption);
              }
            }
          });

          setFromWalletOptions(fromOptions);
          setToWalletOptions(toOptions);
        } else {
          setFromWalletOptions([]);
          setToWalletOptions([]);
        }
      } catch (error) {
        showError("Error Occurred", error.message || "Failed to load data");
        setFromWalletOptions([]);
        setToWalletOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - user is loaded inside the effect

  useEffect(() => {
    if (prefillAppliedRef.current || loading || !transferMode) return;
    if (fromWalletOptions.length === 0 && toWalletOptions.length === 0) return;

    if (transferMode === "deposit") {
      const usdFromOption = findUsdWalletOption(fromWalletOptions);
      if (usdFromOption) setFromWallet(usdFromOption);
      const toMatch = findWalletOptionById(toWalletOptions, preselectWalletId);
      if (toMatch) setToWallet(toMatch);
      prefillAppliedRef.current = true;
      return;
    }

    if (transferMode === "withdraw" || transferMode === "transfer") {
      if (transferMode === "withdraw") {
        const usdToOption = findUsdWalletOption(toWalletOptions);
        if (usdToOption) setToWallet(usdToOption);
      }
      const fromMatch = findWalletOptionById(fromWalletOptions, preselectWalletId);
      if (fromMatch) setFromWallet(fromMatch);
      prefillAppliedRef.current = true;
    }
  }, [loading, transferMode, preselectWalletId, fromWalletOptions, toWalletOptions]);


  const handleAmountChange = (e) => {
    setAmount(e.target.value);
    if (errors.amount) {
      setErrors({ ...errors, amount: null });
    }
  };

  const handleFromWalletChange = (value) => {
    setFromWallet(value);
    if (errors.fromWallet) {
      setErrors({ ...errors, fromWallet: null });
    }
  };

  const handleToWalletChange = (value) => {
    setToWallet(value);
    if (errors.toWallet) {
      setErrors({ ...errors, toWallet: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!fromWallet) {
      newErrors.fromWallet = "From account is required";
    }
    if (!toWallet) {
      newErrors.toWallet = "To account is required";
    }

    // Prevent selecting the same wallet for from/to
    const fromId = fromWallet && typeof fromWallet === "object" ? fromWallet.value : fromWallet;
    const toId = toWallet && typeof toWallet === "object" ? toWallet.value : toWallet;
    if (fromId && toId && String(fromId) === String(toId)) {
      newErrors.toWallet = "Please select a different destination account";
    }

    if (!amount || amount.trim() === "") {
      newErrors.amount = "Amount is required";
    } else {
      const value = parseFloat(amount.replace(/,/g, "").trim());
      if (Number.isNaN(value) || value <= 0) {
        newErrors.amount = "Amount must be a valid positive number";
      } else {
        const selectedTo = toWalletOptions.find((w) => w.value === toId);
        const minAmount = getWalletMinAmount(selectedTo?.data);
        // API requires amount > MIN (equal to MIN is rejected).
        if (minAmount != null && value <= minAmount) {
          newErrors.amount = `Minimum Amount:${minAmount}$`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceed = async () => {
    if (!canInternalTransfer) {
      showError("Error Occurred", "You don't have permission to make internal transfer.");
      return;
    }

    if (!validateForm()) {
      return;
    }

    const amountValue = parseFloat(amount.replace(/,/g, "").trim());
    const fromId = fromWallet && typeof fromWallet === "object" ? fromWallet.value : fromWallet;
    const toId = toWallet && typeof toWallet === "object" ? toWallet.value : toWallet;

    const selectedFrom = fromWalletOptions.find((w) => w.value === fromId);
    const selectedTo = toWalletOptions.find((w) => w.value === toId);

    // Check MT5->MT5 transfer permission (mirror Flutter logic from lines 349-360)
    // Get currency type: 0=FIAT, 3=TRADING (matches Flutter CurrencyType enum)
    const fromCurrencyType =
      selectedFrom?.data?.currencies?.TYPE ||
      selectedFrom?.data?.currency?.type ||
      selectedFrom?.data?.CURRENCY_TYPE ||
      selectedFrom?.data?.currencyType ||
      null;
    const toCurrencyType =
      selectedTo?.data?.currencies?.TYPE ||
      selectedTo?.data?.currency?.type ||
      selectedTo?.data?.CURRENCY_TYPE ||
      selectedTo?.data?.currencyType ||
      null;

    // Get trading IDs as fallback to determine if wallet is TRADING type
    const fromTradingId =
      selectedFrom?.data?.account?.account_information?.TRADING_ID ||
      selectedFrom?.data?.account?.accountInformation?.tradingId ||
      selectedFrom?.data?.TRADING_ID ||
      selectedFrom?.data?.trading_id ||
      null;
    const toTradingId =
      selectedTo?.data?.account?.account_information?.TRADING_ID ||
      selectedTo?.data?.account?.accountInformation?.tradingId ||
      selectedTo?.data?.TRADING_ID ||
      selectedTo?.data?.trading_id ||
      null;

    // Determine if wallets are TRADING type (same logic as wallet loading)
    const isFromTrading = fromCurrencyType === 3 || (fromCurrencyType === null && fromTradingId);
    const isToTrading = toCurrencyType === 3 || (toCurrencyType === null && toTradingId);

    // If both wallets are TRADING type, check MT5->MT5 permission
    // This matches Flutter: !canTransferMt5Mt5 && both are TRADING type
    if (isFromTrading && isToTrading && !canTransferMt5Mt5) {
      showError("Error Occurred", "You don't have permission to transfer from MT5 to MT5.");
      return;
    }

    // Check balance if available (similar to Flutter)
    const fromBalanceRaw =
      selectedFrom?.data?.CURRENT_BALENCE ||
      selectedFrom?.data?.CURRENT_BALANCE ||
      selectedFrom?.data?.BALANCE ||
      selectedFrom?.data?.AMOUNT ||
      0;
    const fromBalance = typeof fromBalanceRaw === "string"
      ? parseFloat(fromBalanceRaw.replace(/,/g, ""))
      : parseFloat(fromBalanceRaw);

    if (!Number.isNaN(fromBalance) && amountValue > fromBalance) {
      showError("Error Occurred", "You don't have enough amount.", { skipLogout: true });
      return;
    }

    try {
      setProcessing(true);
      setErrors({});

      const payload = {
        amount: amountValue,
        fromWalletId: fromId,
        toWalletId: toId,
      };

      const res = await createInternalTransferMoneyOperation(payload);
      const d = res.decryptedData || res.data || {};
      console.log("[InternalTransfer] createInternalTransferMoneyOperation response:", {
        rawResponse: res,
        parsedData: d,
      });
      const code = Number(d.CODE);

      if (code === AppConstants.responseOk || code === 200) {
        const transactionId = d.TRANSACTION_ID;
        const received =
          typeof d.RECEIVED_AMOUNT === "number"
            ? d.RECEIVED_AMOUNT
            : parseFloat(d.RECEIVED_AMOUNT) || 0;

        const fromSymbol =
          selectedFrom?.data?.currencies?.CURRENCY_SYMBOL ||
          selectedFrom?.data?.CURRENCY_SYMBOL ||
          "";
        const toSymbol =
          selectedTo?.data?.currencies?.CURRENCY_SYMBOL ||
          selectedTo?.data?.CURRENCY_SYMBOL ||
          "";

        const firstMessage = `You will transfer ${amountValue} ${fromSymbol || ""} from ${
          selectedFrom?.label || "source account"
        } to ${selectedTo?.label || "destination account"}. You will receive ${received} ${
          toSymbol || ""
        } after commission.`;

        pendingTransferRef.current = {
          fromWalletId: fromId,
          toWalletId: toId,
          amount: amountValue,
          receivedAmount: received > 0 ? received : amountValue,
        };

        setPasswordDialogData({
          requestToken: transactionId,
          firstMessage,
          secondMessage: "",
        });
        setShowPasswordPopup(true);
      } else if (code === AppConstants.responseNeedPermission) {
        showError("Error Occurred", "You don't have access to do this operation.");
      } else if (code === AppConstants.responseWaiting) {
        showWarning("Action Required", "Financial data should be added to use this method.");
      } else if (code === AppConstants.responseFailed) {
        showError("Error Occurred", d.MESSAGE || "You don't have enough amount.", {
          skipLogout: true,
        });
      } else {
        showError("Error Occurred", d.MESSAGE || "Please try again.");
      }
    } catch (error) {
      console.error("[InternalTransfer] createInternalTransferMoneyOperation error:", {
        error,
        responseData: error?.response?.data,
      });
      showError("Error Occurred", error.message || "Failed to process internal transfer. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const applyOptimisticTransferBalances = useCallback(
    ({ fromWalletId, toWalletId, debitAmount, creditAmount }) => {
      const patchedRows = [];

      const patchOptionList = (prev, walletId, delta) => {
        if (!walletId || !Number.isFinite(Number(delta)) || Number(delta) === 0) {
          return prev;
        }

        return prev.map((opt) => {
          if (String(opt.value) !== String(walletId)) return opt;

          const data = { ...(opt.data || {}) };
          const current = getWalletBalance(data);
          const nextBalance = Math.max(0, current + Number(delta));
          data.CURRENT_BALENCE = nextBalance;
          data.CURRENT_BALANCE = nextBalance;
          data.AMOUNT = nextBalance;
          data.amount = nextBalance;
          data.BALANCE = nextBalance;

          if (isFiatWalletItem(data)) {
            patchedRows.push(data);
          }

          return {
            ...opt,
            label: buildWalletOptionLabel(data, nextBalance),
            data,
          };
        });
      };

      setFromWalletOptions((prev) => {
        const next = patchOptionList(prev, fromWalletId, -Math.abs(Number(debitAmount) || 0));
        const withCredit = patchOptionList(next, toWalletId, Math.abs(Number(creditAmount) || 0));
        fromWalletOptionsRef.current = withCredit;
        return withCredit;
      });

      setToWalletOptions((prev) => {
        const next = patchOptionList(prev, fromWalletId, -Math.abs(Number(debitAmount) || 0));
        const withCredit = patchOptionList(next, toWalletId, Math.abs(Number(creditAmount) || 0));
        toWalletOptionsRef.current = withCredit;
        return withCredit;
      });

      if (patchedRows.length > 0) {
        const byId = new Map();
        patchedRows.forEach((row) => {
          const id = row?.ID != null ? String(row.ID) : row?.id != null ? String(row.id) : null;
          if (id) byId.set(id, row);
        });
        WalletController.patchCachedWalletBalances(Array.from(byId.values()));
      }
    },
    []
  );

  const refreshWalletDropdowns = useCallback(async () => {
    try {
      const previousRows = [
        ...(fromWalletOptionsRef.current || []),
        ...(toWalletOptionsRef.current || []),
      ]
        .map((opt) => ({ ...(opt.data || {}) }))
        .filter((row) => row?.ID != null || row?.id != null);

      let amountById = new Map();
      try {
        let amountResult = null;
        if (previousRows.length > 0) {
          amountResult = await WalletController.updateEWalletsAmount(previousRows);
        } else {
          amountResult = await WalletController.refreshWalletsCache();
        }

        const amountWallets = amountResult?.wallets || [];
        amountWallets.forEach((w) => {
          const id = w?.ID != null ? String(w.ID) : w?.id != null ? String(w.id) : null;
          if (!id) return;
          amountById.set(id, getWalletBalance(w));
        });

        if (amountWallets.length > 0) {
          WalletController.patchCachedWalletBalances(amountWallets);
        }
      } catch (_) {
        // Fall through to TRX rebuild even if amount refresh fails.
      }

      const currentPermissions = getPermissionControllers(user);
      const currentCanTransferUsdMt5 = hasCustomerPermissionEither(
        currentPermissions.customerPerm,
        currentPermissions.customerDevicePerm,
        PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5
      );
      const currentCanTransferMt5Usd = hasCustomerPermissionEither(
        currentPermissions.customerPerm,
        currentPermissions.customerDevicePerm,
        PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_USD
      );

      const typeParam = [AccountType.REAL, AccountType.BROKER];
      const withUsdParam = 1;
      const walletsResponse = await getTradingAccountsTrx(typeParam, withUsdParam);
      const walletsData = walletsResponse?.decryptedData || walletsResponse?.data;

      if (walletsData && walletsData.CODE === AppConstants.responseOk && walletsData.DATA) {
        const list = Array.isArray(walletsData.DATA) ? walletsData.DATA : [];

        const fromOptions = [];
        const toOptions = [];
        let usdAddedToFromAccounts = false;
        const hideBrokerWithdraw = isAgentWithdrawalDisabled(user);

        list.forEach((item) => {
          const currencyType =
            item.currencies?.TYPE ||
            item.currency?.type ||
            item.CURRENCY_TYPE ||
            item.currencyType ||
            null;
          const accountType =
            item.account?.TYPE ||
            item.account?.type ||
            item.ACCOUNT_TYPE ||
            item.accountType ||
            null;
          const tradingId =
            item.account?.account_information?.TRADING_ID ||
            item.account?.accountInformation?.tradingId ||
            item.TRADING_ID ||
            item.trading_id ||
            null;
          const value = item.ID ?? item.id ?? item.WALLET_ID;
          const walletIdKey = value != null ? String(value) : null;
          const mergedBalance =
            walletIdKey && amountById.has(walletIdKey)
              ? amountById.get(walletIdKey)
              : getWalletBalance(item);
          const formattedBalance = Number.isNaN(Number(mergedBalance)) ? 0 : Number(mergedBalance);
          const itemWithBalance = {
            ...item,
            CURRENT_BALENCE: formattedBalance,
            CURRENT_BALANCE: formattedBalance,
            AMOUNT: formattedBalance,
            amount: formattedBalance,
            BALANCE: formattedBalance,
          };
          const isTradingAccount = currencyType === 3 || (currencyType === null && tradingId);
          const isFiatWallet = currencyType === 0 || (currencyType === null && !tradingId);

          if (
            isTradingAccount &&
            (accountType === null || accountType === AccountType.REAL || accountType === AccountType.BROKER)
          ) {
            const tradingLabel = buildWalletOptionLabel(itemWithBalance, formattedBalance);
            const tradingOption = {
              value,
              label: tradingLabel,
              data: itemWithBalance,
            };
            // From = withdraw source: omit broker when AGENT_WITHDRAWAL_DISABLED is checked.
            if (!(accountType === AccountType.BROKER && hideBrokerWithdraw)) {
              fromOptions.push(tradingOption);
            }
            toOptions.push({ ...tradingOption });
          } else if (isFiatWallet) {
            const usdLabel = buildWalletOptionLabel(itemWithBalance, formattedBalance);
            const usdOption = {
              value,
              label: usdLabel,
              data: itemWithBalance,
            };
            if (!usdAddedToFromAccounts && currentCanTransferUsdMt5) {
              fromOptions.push(usdOption);
              usdAddedToFromAccounts = true;
            }
            if (currentCanTransferMt5Usd) {
              toOptions.push(usdOption);
            }
          }
        });

        fromWalletOptionsRef.current = fromOptions;
        toWalletOptionsRef.current = toOptions;
        setFromWalletOptions(fromOptions);
        setToWalletOptions(toOptions);
        return true;
      }
      return false;
    } catch (_) {
      return false;
    }
  }, [user]);

  const scheduleBalanceRefresh = useCallback(() => {
    balanceRefreshTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    balanceRefreshTimersRef.current = [];

    const runRefresh = () => {
      refreshWalletDropdowns();
    };

    runRefresh();
    [250, 700, 1500].forEach((delay) => {
      const timerId = window.setTimeout(runRefresh, delay);
      balanceRefreshTimersRef.current.push(timerId);
    });

    try {
      window.dispatchEvent(new Event("mfg:funds-refresh"));
      window.dispatchEvent(new Event("dashboard:refresh"));
    } catch (_) {}
  }, [refreshWalletDropdowns]);

  const handlePasswordDialogClose = async (result) => {
    setShowPasswordPopup(false);
    setPasswordDialogData(null);
    const pendingTransfer = pendingTransferRef.current;
    pendingTransferRef.current = null;

    if (result === "ok" || result === "need_admin") {
      clearHomePageInfosClientCache();

      if (
        result === "ok" &&
        pendingTransfer?.fromWalletId &&
        pendingTransfer?.toWalletId &&
        pendingTransfer?.amount > 0
      ) {
        applyOptimisticTransferBalances({
          fromWalletId: pendingTransfer.fromWalletId,
          toWalletId: pendingTransfer.toWalletId,
          debitAmount: pendingTransfer.amount,
          creditAmount: pendingTransfer.receivedAmount || pendingTransfer.amount,
        });
      }

      showWarning(
        "Request Submitted",
        result === "ok" ? "Transfer completed successfully." : "Transfer is pending admin approval."
      );
      setErrors({});
      setAmount("");
      setFromWallet(null);
      setToWallet(null);
      scheduleBalanceRefresh();
    } else if (result === "canceled") {
      showWarning("Action Canceled", "Transaction canceled.");
      setErrors({});
      setAmount("");
      setFromWallet(null);
      setToWallet(null);
    } else if (result === "failed") {
      showError("Error Occurred", "Transaction failed. Please try again.");
      await refreshWalletDropdowns();
    }
  };

  useEffect(() => {
    const onWalletRefresh = () => {
      refreshWalletDropdowns();
    };
    window.addEventListener("mfg:funds-refresh", onWalletRefresh);
    window.addEventListener("dashboard:refresh", onWalletRefresh);
    return () => {
      window.removeEventListener("mfg:funds-refresh", onWalletRefresh);
      window.removeEventListener("dashboard:refresh", onWalletRefresh);
    };
  }, [refreshWalletDropdowns]);

  return (
    <div className="cash-in-page">
      <Sidebar user={user} />
      <div className="cash-in-content">
        {loading && (
          <div className="cash-in-loading-message">{t("loading", { ns: "mobile" })}...</div>
        )}

        <div className="cash-in-container cash-in-container--styled">
          {!canInternalTransfer ? (
            <div className="cash-in-tab-content cash-in-tab-content--styled">
              <div className="cash-in-locked-message">
                <i className="fa-solid fa-lock"></i>
                <p>{t("you_dont_have_access_to_do_this_operation", { ns: "mobile" })}</p>
              </div>
            </div>
          ) : (
            <div className="cash-in-tab-content cash-in-tab-content--styled">
              <div className="cash-in-layout-grid">
                <div className="cash-in-form-panel">
                  <div className="cash-in-panel-header">
                    <h2>{t("internal_transfer", { ns: "mobile" })}</h2>
                    <p>{t("internal_transfer_description", { ns: "mobile" })}</p>
                  </div>

                  <div className="cash-in-form">
                    <div className="cash-in-form-group" data-field-tour="transfer:amount">
                      <label className="cash-in-form-label">{t("amount", { ns: "mobile" })}</label>
                      <div className="cash-in-input-wrapper">
                        <span className="cash-in-input-icon">
                          <i className="fa-solid fa-dollar-sign"></i>
                        </span>
                        <CustomInput
                          type="text"
                          placeholder={t("enter_the_amount", { ns: "mobile" })}
                          value={amount}
                          onChange={handleAmountChange}
                          className={`cash-in-input ${
                            errors.amount ? "cash-in-input-error" : ""
                          }`}
                          disabled={processing}
                        />
                      </div>
                      {errors.amount && (
                        <div className="cash-in-field-error">{errors.amount}</div>
                      )}
                    </div>

                    <div className="cash-in-form-group" data-field-tour="transfer:from">
                      <label className="cash-in-form-label">{t("from_account", { ns: "mobile" })}</label>
                      <CustomDropdown
                        options={fromWalletOptions}
                        value={fromWallet}
                        onChange={handleFromWalletChange}
                        placeholder={t("select_source_account", { ns: "mobile" })}
                        name="fromWallet"
                        className={`cash-in-dropdown ${
                          errors.fromWallet ? "dropdown-error" : ""
                        }`}
                        disabled={processing}
                        searchable={true}
                        getDisplayValue={(opt) =>
                          opt && typeof opt === "object" && opt.label != null
                            ? String(opt.label)
                            : ""
                        }
                        renderOption={(option, isSelected) => {
                          const item = option?.data;
                          return (
                            <div
                              className={`cash-in-wallet-option${
                                isTradingAccountItem(item) ? " cash-in-account-option" : ""
                              }`}
                              style={{ display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              {renderAccountOptionLeading(item)}
                              <span className="cash-in-account-text">{option?.label ?? ""}</span>
                              {isSelected && (
                                <i className="fa-solid fa-check custom-dropdown-check"></i>
                              )}
                            </div>
                          );
                        }}
                        renderValue={(option, placeholderText) => {
                          if (!option) {
                            return (
                              <span className="custom-dropdown-placeholder">
                                {placeholderText || t("select_source_account", { ns: "mobile" })}
                              </span>
                            );
                          }
                          const item = option?.data;
                          return (
                            <div
                              className={
                                isTradingAccountItem(item) ? "cash-in-account-trigger" : undefined
                              }
                              style={{ display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              {renderAccountOptionLeading(item)}
                              <span className="cash-in-account-text">{option.label}</span>
                            </div>
                          );
                        }}
                      />
                      {errors.fromWallet && (
                        <div className="cash-in-field-error">{errors.fromWallet}</div>
                      )}
                    </div>

                    <div className="cash-in-form-group" data-field-tour="transfer:to">
                      <label className="cash-in-form-label">{t("to_account", { ns: "mobile" })}</label>
                      <CustomDropdown
                        options={toWalletOptions}
                        value={toWallet}
                        onChange={handleToWalletChange}
                        placeholder={t("select_destination_account", { ns: "mobile" })}
                        name="toWallet"
                        className={`cash-in-dropdown ${
                          errors.toWallet ? "dropdown-error" : ""
                        }`}
                        disabled={processing}
                        searchable={true}
                        getDisplayValue={(opt) =>
                          opt && typeof opt === "object" && opt.label != null
                            ? String(opt.label)
                            : ""
                        }
                        renderOption={(option, isSelected) => {
                          const item = option?.data;
                          return (
                            <div
                              className={`cash-in-wallet-option${
                                isTradingAccountItem(item) ? " cash-in-account-option" : ""
                              }`}
                              style={{ display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              {renderAccountOptionLeading(item)}
                              <span className="cash-in-account-text">{option?.label ?? ""}</span>
                              {isSelected && (
                                <i className="fa-solid fa-check custom-dropdown-check"></i>
                              )}
                            </div>
                          );
                        }}
                        renderValue={(option, placeholderText) => {
                          if (!option) {
                            return (
                              <span className="custom-dropdown-placeholder">
                                {placeholderText || t("select_destination_account", { ns: "mobile" })}
                              </span>
                            );
                          }
                          const item = option?.data;
                          return (
                            <div
                              className={
                                isTradingAccountItem(item) ? "cash-in-account-trigger" : undefined
                              }
                              style={{ display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              {renderAccountOptionLeading(item)}
                              <span className="cash-in-account-text">{option.label}</span>
                            </div>
                          );
                        }}
                      />
                      {errors.toWallet && (
                        <div className="cash-in-field-error">{errors.toWallet}</div>
                      )}
                    </div>
                  </div>

                  <div className="cash-in-actions">
                    <CustomButton
                      className="cash-in-proceed-button"
                      variant="primary"
                      onClick={handleProceed}
                      disabled={processing}
                    >
                      <span>{processing ? t("processing", { ns: "mobile" }) + "..." : t("proceed", { ns: "mobile" })}</span>
                    </CustomButton>
                  </div>
                </div>

                <div className="cash-in-side-panel">
                  <div className="cash-in-info-card" data-field-tour="transfer:summary">
                    <h3>
                      <i className="fa-solid fa-arrow-right-arrow-left" />
                      {t("transfer_summary", { ns: "mobile" })}
                    </h3>
                    <ul>
                      <li>
                        <span>{t("source_account", { ns: "mobile" })}</span>
                        <strong>{(fromWallet && fromWallet.label) || "-"}</strong>
                      </li>
                      <li>
                        <span>{t("destination_account", { ns: "mobile" })}</span>
                        <strong>{(toWallet && toWallet.label) || "-"}</strong>
                      </li>
                      <li>
                        <span>{t("amount", { ns: "mobile" })}</span>
                        <strong>{amount || "-"}</strong>
                      </li>
                    </ul>
                  </div>

                  <div className="cash-in-help-card">
                    <h3>
                      <i className="fa-solid fa-headset" />
                      {t("contactus_title", { ns: "mobile" })}
                    </h3>
                    <p>
                      {t("internal_transfer_help", { ns: "mobile" })}
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        openSupportChatOnCurrentPage(
                          navigate,
                          location.pathname,
                          location.search
                        )
                      }
                    >
                      {t("about_live_chat_value", { ns: "mobile" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <TransactionPasswordDialog
        open={showPasswordPopup && !!passwordDialogData}
        onClose={handlePasswordDialogClose}
        mode="internal_transfer"
        requestToken={passwordDialogData?.requestToken ?? ""}
        firstMessage={passwordDialogData?.firstMessage ?? ""}
        secondMessage={passwordDialogData?.secondMessage ?? ""}
      />
    </div>
  );
}

